import React, { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../../services/dashboardService';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const TrustTrendsWidget = () => {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('weekly');

  const fetchTrendData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getTrustTrends(period);
      setTrendData(response.data);
    } catch (err) {
      console.error('Error fetching trust trends:', err);
      setError('Failed to load trust trends');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchTrendData();
  }, [fetchTrendData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">
            {formatDate(data.week_start)} - {formatDate(data.week_end)}
          </p>
          <div className="space-y-1 text-sm">
            <p className="text-gray-600">
              Balance: <span className="font-medium text-gray-900">{formatCurrency(data.ending_balance)}</span>
            </p>
            <p className="text-green-600">
              Deposits: <span className="font-medium">+{formatCurrency(data.deposits)}</span>
            </p>
            <p className="text-red-600">
              Withdrawals: <span className="font-medium">-{formatCurrency(data.withdrawals)}</span>
            </p>
            <p className={data.net_change >= 0 ? 'text-green-600' : 'text-red-600'}>
              Net: <span className="font-medium">{data.net_change >= 0 ? '+' : ''}{formatCurrency(data.net_change)}</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 lg:row-span-2">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 w-32 bg-gray-200 rounded"></div>
            <div className="h-8 w-24 bg-gray-200 rounded"></div>
          </div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 lg:row-span-2">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <TrendingUp className="mr-2 h-5 w-5 text-yellow-600" />
            Trust Balance Trends
          </h3>
          <button
            onClick={fetchTrendData}
            className="text-yellow-600 hover:text-gray-800 text-sm"
          >
            Retry
          </button>
        </div>
        <div className="text-center py-8">
          <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const chartData = trendData?.data_points?.map(point => ({
    ...point,
    date: formatDate(point.week_start),
    ending_balance: parseFloat(point.ending_balance),
    deposits: parseFloat(point.deposits),
    withdrawals: parseFloat(point.withdrawals),
    net_change: parseFloat(point.net_change)
  })) || [];

  return (
    <div className="bg-white rounded-lg shadow p-6 lg:row-span-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <TrendingUp className="mr-2 h-5 w-5 text-yellow-600" />
          Trust Balance Trends
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPeriod('weekly')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              period === 'weekly'
                ? 'bg-yellow-100 text-yellow-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Weekly
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              period === 'monthly'
                ? 'bg-yellow-100 text-yellow-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500">Average Balance</p>
          <p className="text-lg font-bold text-gray-900">
            {formatCurrency(trendData?.average_balance || 0)}
          </p>
        </div>
        <div className={`rounded-lg p-3 ${
          parseFloat(trendData?.total_change || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <p className="text-xs text-gray-500">Net Change</p>
          <p className={`text-lg font-bold ${
            parseFloat(trendData?.total_change || 0) >= 0 ? 'text-green-700' : 'text-red-700'
          }`}>
            {parseFloat(trendData?.total_change || 0) >= 0 ? '+' : ''}
            {formatCurrency(trendData?.total_change || 0)}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-48">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#CA8A04" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#CA8A04" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                width={50}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="ending_balance"
                stroke="#CA8A04"
                strokeWidth={2}
                fill="url(#colorBalance)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p className="text-sm">No trend data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustTrendsWidget;
